import React, { useEffect, useState, useContext } from 'react';
import Header from '../Header';
import { UserContext } from "../UserContext";
import apiClient from '../../api/client';
import { useNavigate } from 'react-router-dom';
import './index.css';
import { toast } from 'react-toastify';
import { FaBox } from 'react-icons/fa';
import { FiLogIn } from 'react-icons/fi';
import Pagination from '../Pagination';
import PhoneCell from '../PhoneCell';
import TruncatedText from '../TruncatedText';
import LoadError from '../LoadError';
import { getErrorMessage } from '../../utils/network';
import OperatorDevicesPanel from '../NmsDevices/OperatorDevicesPanel';

const OperatorList = () => {
  const { user, token, tempLogin } = useContext(UserContext);
  const [operators, setOperators] = useState([]);
  const [errorCause, setErrorCause] = useState(null);
  // Kept for the session, so coming back from an account page shows the same search.
  const [searchTerm, setSearchTerm] = useState(() => {
    try { return sessionStorage.getItem('operators_search') || ''; } catch { return ''; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('operators_search', searchTerm); } catch { /* private mode */ }
  }, [searchTerm]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balancePassword, setBalancePassword] = useState('');
  const [remarks, setRemarks] = useState('');
  const [showBalanceForm, setShowBalanceForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Profile modal view: 'profile' | 'devices' | 'session' (terminal/web iframe)
  const [opmView, setOpmView] = useState('profile');
  const isSuperAdmin = user.role === 'superadmin';

  const openAddBalanceForm = (operator) => {
    setSelectedOperator(operator);
    setBalanceAmount('');
    setRemarks('');
    setShowBalanceForm(true);
  };

  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    username: '',
    password: '',
    networkName: '',
    operatorName: '',
    phoneNumber: '',
    Email: '',
    address: '',
    packPrice: '',
    internetEnabled: '',
    internetBaseUrl: '',
    internetToken: '',
    pioneeriptvOprId: '',
    pioneeriptvToken: '',
    dvplEnabled: '',
    dvplBaseUrl: '',
    dvplToken: '',
    dvplPartnerId: '',
    dvplBranchId: ''
  });

  const navigate = useNavigate();

  // Server-side pages: only one page of accounts is loaded at a time.
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    try { return Number(sessionStorage.getItem('accounts_per_page')) || 25; } catch { return 25; }
  });
  const [total, setTotal] = useState(0);
  const reqSeq = React.useRef(0);

  const loadPage = async (p = page, q = searchTerm, size = perPage) => {
    const my = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await apiClient.get('/operator_api.php', {
        params: { filterRole: 'operator', page: p, per_page: size, q: q.trim() },
      });
      if (my !== reqSeq.current) return;
      setOperators(data.operators || []);
      setTotal(Number(data.total || 0));
      if (data.page && data.page !== p) setPage(data.page);
      setError(null);
      setErrorCause(null);
    } catch (err) {
      if (my !== reqSeq.current) return;
      setError(getErrorMessage(err, 'Could not load the list.'));
      setErrorCause(err);
    } finally {
      if (my === reqSeq.current) setLoading(false);
    }
  };
  const fetchOperators = () => loadPage(page, searchTerm, perPage);
  const fetchOperatorsSearch = () => loadPage(page, searchTerm, perPage);

  // Load on open, and again (from page 1) a moment after the search text changes
  const firstLoad = React.useRef(true);
  useEffect(() => {
    if (!token) return undefined;
    if (firstLoad.current) {
      firstLoad.current = false;
      loadPage(1, searchTerm, perPage);
      return undefined;
    }
    const t = setTimeout(() => { setPage(1); loadPage(1, searchTerm, perPage); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchTerm]);

  const goPage = (p) => { setPage(p); loadPage(p, searchTerm, perPage); };
  const changePerPage = (n) => {
    try { sessionStorage.setItem('accounts_per_page', String(n)); } catch { /* ignore */ }
    setPerPage(n);
    setPage(1);
    loadPage(1, searchTerm, n);
  };

  const handleAddBalance = async () => {
    if (!balanceAmount || isNaN(balanceAmount)) {
      toast.warn("Please enter a valid amount.");
      return;
    }
    if (!balancePassword) {
      toast.warn("Please enter your Wallet Password to confirm.");
      return;
    }

    const added_by = `${user.operatorName}, ${user.phoneNumber}`;
    setLoading(true);
    try {
      const response = await apiClient.post(
        "/addBalance.php",
        {
          id: selectedOperator.id,
          amount: parseFloat(balanceAmount), // safer than parseInt
          added_by,
          remarks,
          walletPassword: balancePassword,
        }
      );

      if (response.data.status === "success") {
        toast.success("Balance updated successfully!");
        searchTerm ? fetchOperatorsSearch(searchTerm) : fetchOperators();
        setShowBalanceForm(false);
        setBalancePassword('');
        closeProfileModal();
      } else {
        toast.error("Failed: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Add balance error:", error);
      toast.error(getErrorMessage(error, "Failed to update balance. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const openEditOperatorForm = (operator) => {
    setEditForm({
      id: operator.id,
      username: operator.username,
      password: '',
      networkName: operator.networkName,
      operatorName: operator.operatorName,
      phoneNumber: operator.phoneNumber,
      Email: operator.Email,
      address: operator.address,
      packPrice: operator.packPrice,
      operCode: operator.operCode,
      watchoOperCode: operator.watchoOperCode,
      internetEnabled: operator.internetEnabled,
      internetBaseUrl: operator.internetBaseUrl,
      internetToken: operator.internetToken,
      railtelEnabled: operator.railtelEnabled,
      operatorEnabled: operator.operatorEnabled,
      pioneeriptvOprId: operator.pioneeriptvOprId,
      pioneeriptvToken: operator.pioneeriptvToken,
      dvplEnabled: operator.dvplEnabled,
      dvplBaseUrl: operator.dvplBaseUrl,
      dvplToken: operator.dvplToken,
      dvplPartnerId: operator.dvplPartnerId,
      dvplBranchId: operator.dvplBranchId,
      role: operator.role,
      created_by: operator.created_by,
    });
    setShowEditForm(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitEditForm = async () => {
    setLoading(true);
    try {
      const res =await apiClient.put('/operator_api.php', editForm);
      console.log(res.data);
      toast.success('Operator updated successfully!');
      searchTerm ? fetchOperatorsSearch(searchTerm) : fetchOperators();
      setShowEditForm(false);
      closeProfileModal();
    } catch (err) {
      // console.error(err);
      toast.warn(getErrorMessage(err, 'Failed to update operator'));
    } finally {
      setLoading(false)
    }
  };

  const openProfileModal = (operator) => {
    setSelectedOperator(operator);
    setOpmView('profile');
  };

  const closeProfileModal = () => {
    setSelectedOperator(null);
    setStatusConfirm(false);
    setOpmView('profile');
  };

  // ---- Enable / disable account (Status in the detail window) ----
  const [statusConfirm, setStatusConfirm] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  // Mirrors toggle_operator_status.php: superadmin can change any admin or
  // operator; an admin only the operators it created; nobody their own.
  const canToggleStatus = (acc) => {
    if (!acc || Number(acc.id) === Number(user.operatorId)) return false;
    const r = String(acc.role || '').toLowerCase();
    if (user.role === 'superadmin') return r === 'admin' || r === 'operator';
    if (user.role === 'admin') return r === 'operator' && Number(acc.created_by) === Number(user.operatorId);
    return false;
  };

  const confirmToggleStatus = async () => {
    if (!selectedOperator) return;
    const next = Number(selectedOperator.operatorEnabled) === 1 ? 0 : 1;
    setStatusSaving(true);
    try {
      const { data } = await apiClient.post('/toggle_operator_status.php', {
        id: selectedOperator.id,
        enabled: next,
      });
      if (!data.success) throw new Error(data.message);
      const apply = (o) => (o.id === selectedOperator.id ? { ...o, operatorEnabled: next } : o);
      setOperators((list) => list.map(apply));
      setSelectedOperator((o) => (o ? { ...o, operatorEnabled: next } : o));
      toast.success(data.message || (next ? 'Account enabled' : 'Account disabled'));
      setStatusConfirm(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not update the account status'));
    } finally {
      setStatusSaving(false);
    }
  };

  const goTo = (path) => {
    closeProfileModal();
    navigate(path);
  };

  const tempLoginOperator = async (targetUserId) => {
    closeProfileModal();

    let res;
    try {
      res = await apiClient.post(
        "/impersonate.php",
        { targetUserId },
        { validateStatus: () => true }
      );
    } catch (err) {
      // No internet: previously an uncaught error and nothing happened.
      toast.error(getErrorMessage(err, "Could not log in as this account"));
      return;
    }

    const data = res.data;

    if (res.status >= 200 && res.status < 300 && data.token) {
      tempLogin(data.token);

      if (data.role === "admin") {
        window.location.href = "/admin";
      } else if (data.role === "operator") {
        window.location.href = "/operator";
      }
    } else {
      toast.warn("Impersonation failed: " + (data.error || "Unknown error"));
    }
  };

  const isCustomChannelMode = (channelMode) => {
    return channelMode && channelMode.toLowerCase() === 'custom';
  };

  const filteredOperators = operators.filter((operator) => {
    const search = searchTerm.toLowerCase();

    return (
      operator.username?.toLowerCase().includes(search) ||
      operator.networkName?.toLowerCase().includes(search) ||
      operator.operatorName?.toLowerCase().includes(search) ||
      operator.phoneNumber?.toLowerCase().includes(search) ||
      operator.address?.toLowerCase().includes(search)
    );
  });

  const highlightText = (text, search) => {
    if (!search || !text) return text;

    // Escaped (typing "(" used to crash the page) and compared without
    // regex.test(), whose /g lastIndex made every other match unhighlighted.
    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.toString().split(new RegExp(`(${escaped})`, "gi"));

    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <mark
          key={index}
          style={{
            backgroundColor: "#a16207",
            padding: "0 2px",
            borderRadius: "2px"
          }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <>
      <Header />

      <div className="operator-header">
        <h2>Operators</h2>
        <input
          type="text"
          placeholder="Search by name or phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button disabled={loading} className="add-operator-btn" onClick={() => navigate('/add-operator')}>
          + Add Operator
        </button>
      </div>

      {error ? (
        <LoadError
          error={errorCause}
          message={error}
          onRetry={() => {
            setLoading(true);
            searchTerm ? fetchOperatorsSearch(searchTerm) : fetchOperators();
          }}
        />
      ) : (
        loading ? (
          <>
            <div className="skeleton-loader">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="skeleton-row">
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                  <div className="skeleton-cell"></div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="operator-list">
            <div className="operator-table-wrapper">
              <table className="operator-table operator-table--compact" id="operatorsTable">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th className="col-center col-login" aria-label="Login as">Login</th>
                    <th>Username</th>
                    <th>Network Name</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Wallet</th>
                    <th className="col-center">Status</th>
                    <th>Created By</th>
                    <th className="col-center">Channel</th>
                    <th className="col-center">Internet</th>
                    <th className="col-center">RailTel</th>
                    <th className="col-center">DVPL</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOperators.length > 0 ? (
                    filteredOperators.map((operator, index) => (
                      <tr
                        key={operator.id}
                        className={index % 2 === 0 ? 'even-row' : 'odd-row'}
                      >
                        <td>{(page - 1) * perPage + index + 1}</td>
                        <td className="col-center col-login">
                          <button
                            type="button"
                            className="oper-login-icon"
                            onClick={() => tempLoginOperator(operator.id)}
                            disabled={loading}
                            title="Login as Operator"
                            aria-label={`Login as ${operator.username}`}
                          >
                            <FiLogIn aria-hidden="true" />
                          </button>
                        </td>
                        <td>
                          <button
                            onClick={() => navigate(`/accounts/${operator.id}`)}
                            disabled={loading}
                            style={{
                              color: '#1f4e8c',
                              textDecoration: 'underline',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              fontWeight: 'bold'
                            }}
                          >
                            <TruncatedText text={operator.username} width={130}>
                              {highlightText(operator.username, searchTerm)}
                            </TruncatedText>
                          </button>
                        </td>
                        <td>
                          <TruncatedText text={operator.networkName} width={170}>
                            {highlightText(operator.networkName, searchTerm)}
                          </TruncatedText>
                        </td>
                        <td>
                          <TruncatedText text={operator.operatorName} width={150}>
                            {highlightText(operator.operatorName, searchTerm)}
                          </TruncatedText>
                        </td>
                        <td>
                          <PhoneCell number={operator.phoneNumber}>
                            {highlightText(operator.phoneNumber, searchTerm)}
                          </PhoneCell>
                        </td>
                        <td>{operator.wallet}</td>
                        <td className="col-center">
                          <span className={`internet-enabled-badge ${operator.operatorEnabled === 1 ? 'enabled' : 'disabled'}`}>
                            {operator.operatorEnabled === 1 ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td><TruncatedText text={operator.created_by_name} width={130} /></td>
                        <td className="col-center">
                          <span className={`channel-mode-badge ${isCustomChannelMode(operator.channel_mode) ? 'custom-mode' : 'default-mode'}`}>
                            {operator.channel_mode || 'default'}
                          </span>
                        </td>
                        <td className="col-center">
                          <span className={`internet-enabled-badge ${operator.internetEnabled === 1 ? 'enabled' : 'disabled'}`}>
                            {operator.internetEnabled === 1 ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="col-center">
                          <span className={`internet-enabled-badge ${operator.railtelEnabled === 1 ? 'enabled' : 'disabled'}`}>
                            {operator.railtelEnabled === 1 ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="col-center">
                          <span className={`internet-enabled-badge ${operator.dvplEnabled === 1 ? 'enabled' : 'disabled'}`}>
                            {operator.dvplEnabled === 1 ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))) : (
                    <tr>
                      <td colSpan="13" style={{ textAlign: 'center', padding: '20px', fontStyle: 'italic' }}>
                        No Operators Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination page={page} perPage={perPage} total={total} loading={loading}
                onPage={goPage} onPerPage={changePerPage} />


              {selectedOperator && (
                <div className="modal-overlay">
                  <div className={`modal-content operator-profile-modal${opmView !== 'profile' ? ' opm--devices' : ''}${opmView === 'session' ? ' opm--session' : ''}`}>

                    <div className="operator-profile-content opm">
                      {/* Compact header */}
                      <div className="opm-head">
                        <div className="opm-avatar">
                          {selectedOperator.operatorName ? selectedOperator.operatorName.charAt(0).toUpperCase() : 'O'}
                        </div>
                        <div className="opm-title">
                          <h2 title={selectedOperator.operatorName}>{selectedOperator.operatorName || 'No Name'}</h2>
                          <p>@{selectedOperator.username} · {selectedOperator.role}</p>
                        </div>
                        <button onClick={closeProfileModal} className="opm-close" aria-label="Close">×</button>
                      </div>

                      {opmView !== 'profile' ? (
                        <OperatorDevicesPanel
                          operator={selectedOperator}
                          onBack={() => setOpmView('profile')}
                          onModeChange={setOpmView}
                        />
                      ) : (
                      <>
                      {/* Details: two columns, label above value */}
                      <dl className="opm-details">
                        <div>
                          <dt>Network name</dt>
                          <dd>{selectedOperator.networkName || '—'}</dd>
                        </div>
                        <div>
                          <dt>Phone</dt>
                          <dd><PhoneCell number={selectedOperator.phoneNumber} /></dd>
                        </div>
                        <div>
                          <dt>Wallet balance</dt>
                          <dd className="opm-wallet">₹{Number(selectedOperator.wallet || 0).toLocaleString('en-IN')}</dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd>
                            {canToggleStatus(selectedOperator) ? (
                              <button
                                type="button"
                                className={`opm-status ${Number(selectedOperator.operatorEnabled) === 1 ? 'is-on' : 'is-off'}`}
                                onClick={() => setStatusConfirm(true)}
                                disabled={statusSaving}
                                title={Number(selectedOperator.operatorEnabled) === 1 ? 'Click to disable this account' : 'Click to enable this account'}
                              >
                                <span className="opm-status__dot" />
                                {Number(selectedOperator.operatorEnabled) === 1 ? 'Enabled' : 'Disabled'}
                                <span className="opm-status__hint">Change</span>
                              </button>
                            ) : (
                              <span className={`opm-status opm-status--static ${Number(selectedOperator.operatorEnabled) === 1 ? 'is-on' : 'is-off'}`}>
                                <span className="opm-status__dot" />
                                {Number(selectedOperator.operatorEnabled) === 1 ? 'Enabled' : 'Disabled'}
                              </span>
                            )}
                          </dd>
                        </div>
                        <div className="opm-full">
                          <dt>Address</dt>
                          <dd>{selectedOperator.address || '—'}</dd>
                        </div>
                      </dl>

                      {/* Action Buttons */}
                      <div className="opm-actions">
                        <button disabled={loading} className='oper-btn oper-btn-edit' onClick={() => openEditOperatorForm(selectedOperator)}>
                          Edit Profile
                        </button>
                        <button disabled={loading} className='oper-btn oper-btn-balance' onClick={() => openAddBalanceForm(selectedOperator)}>
                          Add Balance
                        </button>
                        <button disabled={loading} onClick={() => tempLoginOperator(selectedOperator.id)} className="oper-btn oper-btn-login">
                          Login as Operator
                        </button>
                        <button
                          disabled={loading || !selectedOperator.phoneNumber}
                          onClick={() => setOpmView('devices')}
                          className="oper-btn oper-btn-devices"
                          title={selectedOperator.phoneNumber ? 'OLTs and switches on the NMS' : 'No phone number on this account'}
                        >
                          Devices
                        </button>
                      </div>

                      {/* Package / plan assignment — moved here from the table's
                          Action column so every action for this account lives
                          in one place. Only providers enabled for it show. */}
                      <div className="oper-manage">
                        <h4>Plans &amp; mapping</h4>
                        <div className="oper-manage-grid">
                          <button type="button" onClick={() => goTo(`/assign-plans/${selectedOperator.id}`)}>
                            <FaBox /> <span>Assign Packages</span>
                          </button>
                          {selectedOperator.internetEnabled === 1 && (
                            <>
                              <button type="button" onClick={() => goTo(`/assign-internet-plans/${selectedOperator.id}`)}>
                                <FaBox /> <span>Assign Internet Plans</span>
                              </button>
                              <button type="button" onClick={() => goTo(`/assign-kyc-providers/${selectedOperator.id}`)}>
                                <FaBox /> <span>Assign KYC Providers</span>
                              </button>
                            </>
                          )}
                          {selectedOperator.railtelEnabled === 1 && (
                            <button type="button" onClick={() => goTo(`/assign-railtel-plans/${selectedOperator.id}`)}>
                              <FaBox /> <span>Assign RailTel Plans</span>
                            </button>
                          )}
                          {selectedOperator.dvplEnabled === 1 && (
                            <button type="button" onClick={() => goTo(`/assign-dvpl-plans/${selectedOperator.id}`)}>
                              <FaBox /> <span>Assign DVPL Plans</span>
                            </button>
                          )}
                          <button type="button" onClick={() => goTo(`/assign-pioneeriptv-plans/${selectedOperator.id}`)}>
                            <FaBox /> <span>Assign Pioneer IPTV Plans</span>
                          </button>
                          <button type="button" onClick={() => goTo(`/assign-combos/${selectedOperator.id}`)}>
                            <FaBox /> <span>Assign Combos</span>
                          </button>
                          {selectedOperator.pioneeriptvToken !== null && selectedOperator.pioneeriptvToken !== undefined && (
                            <button type="button" onClick={() => goTo(`/assign-pioneeriptv-branches/${selectedOperator.id}`)}>
                              <FaBox /> <span>Assign Pioneer Branches</span>
                            </button>
                          )}
                        </div>
                      </div>
                      </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {statusConfirm && selectedOperator && (() => {
                const disabling = Number(selectedOperator.operatorEnabled) === 1;
                const name = selectedOperator.operatorName || selectedOperator.username;
                return (
                  <div className="modal-overlay status-confirm-overlay" onClick={() => !statusSaving && setStatusConfirm(false)}>
                    <div className="status-confirm" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                      <div className={`status-confirm__icon ${disabling ? 'danger' : 'ok'}`}>{disabling ? '!' : '✓'}</div>
                      <h3>{disabling ? 'Disable' : 'Enable'} {name}?</h3>
                      <p>
                        {disabling
                          ? `Are you sure you want to disable this ${'operator'}? They will be logged out immediately and won't be able to sign in or recharge until enabled again.`
                          : `Are you sure you want to enable this ${'operator'}? They will be able to sign in and use the portal again.`}
                      </p>
                      <div className="status-confirm__actions">
                        <button type="button" className="status-confirm__cancel" onClick={() => setStatusConfirm(false)} disabled={statusSaving}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={`status-confirm__ok ${disabling ? 'danger' : 'ok'}`}
                          onClick={confirmToggleStatus}
                          disabled={statusSaving}
                        >
                          {statusSaving ? 'Saving…' : disabling ? 'Yes, disable' : 'Yes, enable'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {showBalanceForm && selectedOperator && (
                <div className="modal-overlay">
                  <div className="modal-content">
                    <h3>Add Balance to: {selectedOperator.operatorName}</h3>
                    <p>Current Wallet: ₹{selectedOperator.wallet}</p>
                    <input
                      type="number"
                      placeholder="Enter amount"
                      value={balanceAmount}
                      onChange={(e) => setBalanceAmount(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Remarks"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                    <input
                      type="password"
                      placeholder="Enter your Wallet Password to confirm"
                      value={balancePassword}
                      onChange={(e) => setBalancePassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <p style={{ fontSize: "0.78rem", color: "#64748b", margin: "4px 0 0" }}>
                      Not set up yet? Set your Wallet Password under Change Password.
                    </p>
                    <div className="modal-buttons">
                      <button disabled={loading} className="btn" onClick={handleAddBalance}>Submit</button>
                      <button className="cancel-btn1" onClick={() => { setShowBalanceForm(false); setBalancePassword(''); }}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {showEditForm && (
                <div className="modal-overlay">
                  <div className="modal-content">
                    <h3>Edit Operator: {editForm.operatorName}</h3>
                    <label>Username:</label>
                    <input name="username" placeholder="Username" value={editForm.username} onChange={handleEditChange} />
                    <div className="password-wrapper">
                      <label>Password:</label>
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Leave blank to keep current password"
                        value={editForm.password}
                        onChange={handleEditChange}
                        autoComplete="new-password"
                      />
                      <span
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "👁️" : "👁️‍🗨️"}
                      </span>
                    </div>
                    <label>Network Name:</label>
                    <input name="networkName" placeholder="Network Name" value={editForm.networkName} onChange={handleEditChange} />
                    <label>Operator Name</label>
                    <input name="operatorName" placeholder="Operator Name" value={editForm.operatorName} onChange={handleEditChange} />
                    <label>Phone Number</label>
                    <input name="phoneNumber" placeholder="Phone Number" value={editForm.phoneNumber} onChange={handleEditChange} />
                    <label>Email ID:</label>
                    <input name="Email" placeholder="Email" value={editForm.Email} onChange={handleEditChange} />
                    <label>Address:</label>
                    <input name="address" placeholder="Address" value={editForm.address} onChange={handleEditChange} />

                    {/* 🔐 Only for SuperAdmin */}
                    {isSuperAdmin && (
                      <>
                        <label>Watcho Operator Code:</label>
                        <input name="watchoOperCode" placeholder="Watcho Operator Code" value={editForm.watchoOperCode} onChange={handleEditChange} />

                        <label>Role</label>
                        <select name="role" value={editForm.role} onChange={handleEditChange}>
                          <option value="operator">Operator</option>
                          <option value="admin">Admin</option>
                        </select>

                        {/* Internet Enabled Toggle */}
                        <div className="toggle-wrapper">
                          <label className="toggle-label">
                            <span>Internet Enabled</span>
                            <div className="switch">
                              <input
                                type="checkbox"
                                checked={editForm.internetEnabled === 1} 
                                onChange={(e) =>
                                  setEditForm(prev => ({
                                    ...prev,
                                    internetEnabled: e.target.checked ? 1 : 0
                                  }))
                                }
                              />
                              <span className="slider"></span>
                            </div>
                          </label>
                        </div>

                        {editForm.internetEnabled === 1 && (
                          <div className="internet-section-edit">
                            <div className="internet-fields-edit">
                              <label>Internet Base URL:</label>
                              <input
                                name="internetBaseUrl"
                                value={editForm.internetBaseUrl}
                                onChange={handleEditChange}
                                placeholder="https://radius.example.com"
                              />

                              <label>Internet Token:</label>
                              <input
                                name="internetToken"
                                type="password"
                                value={editForm.internetToken}
                                onChange={handleEditChange}
                                placeholder="Bearer Token"
                                autoComplete="new-password"
                              />
                            </div>
                          </div>
                        )}

                        {/* RailTel Enabled Toggle */}
                        <div className="toggle-wrapper">
                          <label className="toggle-label">
                            <span>RailTel Enabled</span>
                            <div className="switch">
                              <input
                                type="checkbox"
                                checked={editForm.railtelEnabled === 1}
                                onChange={(e) =>
                                  setEditForm(prev => ({
                                    ...prev,
                                    railtelEnabled: e.target.checked ? 1 : 0
                                  }))
                                }
                              />
                              <span className="slider"></span>
                            </div>
                          </label>
                        </div>

                        {/* DVPL Enabled Toggle — same OneRADIUS API shape as
                            Internet, separate reseller credentials */}
                        <div className="toggle-wrapper">
                          <label className="toggle-label">
                            <span>DVPL Enabled</span>
                            <div className="switch">
                              <input
                                type="checkbox"
                                checked={editForm.dvplEnabled === 1}
                                onChange={(e) =>
                                  setEditForm(prev => ({
                                    ...prev,
                                    dvplEnabled: e.target.checked ? 1 : 0
                                  }))
                                }
                              />
                              <span className="slider"></span>
                            </div>
                          </label>
                        </div>

                        {editForm.dvplEnabled === 1 && (
                          <div className="internet-section-edit">
                            <div className="internet-fields-edit">
                              <label>DVPL Base URL:</label>
                              <input
                                name="dvplBaseUrl"
                                value={editForm.dvplBaseUrl}
                                onChange={handleEditChange}
                                placeholder="https://radius.example.com"
                              />

                              <label>DVPL Token:</label>
                              <input
                                name="dvplToken"
                                type="password"
                                value={editForm.dvplToken}
                                onChange={handleEditChange}
                                placeholder="Bearer Token"
                                autoComplete="new-password"
                              />

                              <label>DVPL Partner ID:</label>
                              <input
                                name="dvplPartnerId"
                                value={editForm.dvplPartnerId}
                                onChange={handleEditChange}
                                placeholder="Partner ID"
                              />

                              <label>DVPL Branch ID:</label>
                              <input
                                name="dvplBranchId"
                                value={editForm.dvplBranchId}
                                onChange={handleEditChange}
                                placeholder="Branch ID"
                              />
                            </div>
                          </div>
                        )}

                        {/* Account Enabled Toggle — controls login/API access entirely,
                            not a provider integration. Off means this account can't log
                            in, fetch data, or recharge anything, checked on every request. */}
                        <div className="toggle-wrapper">
                          <label className="toggle-label">
                            <span>Account Enabled</span>
                            <div className="switch">
                              <input
                                type="checkbox"
                                checked={editForm.operatorEnabled === 1}
                                onChange={(e) =>
                                  setEditForm(prev => ({
                                    ...prev,
                                    operatorEnabled: e.target.checked ? 1 : 0
                                  }))
                                }
                              />
                              <span className="slider"></span>
                            </div>
                          </label>
                        </div>

                        <label>Pioneer IPTV Operator ID:</label>
                        <input
                          name="pioneeriptvOprId"
                          value={editForm.pioneeriptvOprId}
                          onChange={handleEditChange}
                          placeholder="Pioneer IPTV Operator ID"
                        />

                        <label>Pioneer IPTV Token:</label>
                        <input
                          name="pioneeriptvToken"
                          type="password"
                          value={editForm.pioneeriptvToken}
                          onChange={handleEditChange}
                          placeholder="Pioneer IPTV Token"
                          autoComplete="new-password"
                        />
                      </>
                    )}

                    <div className="modal-buttons">
                      <button disabled={loading} className="btn" onClick={submitEditForm}>Update</button>
                      <button className="btn cancel-btn" onClick={() => setShowEditForm(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        )
      )}
    </>
  );
};

export default OperatorList;